import React, { useState, useRef, useEffect, useContext } from 'react';
import { Button, Slider, Icon } from 'antd';
import { IoMdAdd, IoMdVolumeHigh, IoMdVolumeOff } from 'react-icons/io';
import FileUpload from '../../../../../components/FileUpload';
import GlobalStateContext from '../../../../../context';
import './MusicPanel.css';

const MusicPanel = ({ 
    onMusicUpdate, 
    currentMusic = {}, 
    musicLibrary = [], 
    onUpload,
    onDelete 
}) => {
    const [gState] = useContext(GlobalStateContext);
    const [isPlaying, setIsPlaying] = useState(false);
    const [volume, setVolume] = useState(0.5);
    const [isMuted, setIsMuted] = useState(false);
    const [selectedTrack, setSelectedTrack] = useState(currentMusic?.selectedTrack || null);
    
    const audioRef = useRef(null);

    const updateMusicState = (musicState) => {
        if (onMusicUpdate) {
            onMusicUpdate({
                music: musicState
            });
        }
    };

    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.volume = isMuted ? 0 : volume;
        }
    }, [volume, isMuted]);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const handleEnded = () => {
            setIsPlaying(false);
        };

        const handleCanPlay = () => {
            console.log('Audio can play - selectedTrack:', selectedTrack?.name, 'isPlaying:', isPlaying);
        };

        audio.addEventListener('ended', handleEnded);
        audio.addEventListener('canplay', handleCanPlay);

        return () => {
            audio.removeEventListener('ended', handleEnded);
            audio.removeEventListener('canplay', handleCanPlay);
        };
    }, [selectedTrack, isPlaying, isMuted, volume]);

    const handleTrackClick = (track) => {
        console.log('Track clicked:', track.name, 'Currently selected:', selectedTrack?.name, 'Is playing:', isPlaying);
        
        // If clicking on the same track, pause/resume
        if ((selectedTrack?.name === track.name )&& audioRef.current) {
            if (isPlaying) {
                audioRef.current.pause();
                setIsPlaying(false);
                updateMusicState({
                    isPlaying: false,
                    selectedTrack: track,
                    volume: isMuted ? 0 : volume
                });
            } else {
                audioRef.current.play().then(() => {
                    setIsPlaying(true);
                    updateMusicState({
                        isPlaying: true,
                        selectedTrack: track,
                        volume: isMuted ? 0 : volume
                    });
                }).catch(error => {
                    console.error('Error playing audio:', error);
                });
            }
        } else {
            // New track selected - set track first, then autoplay after audio loads
            setSelectedTrack(track);
            setIsPlaying(false);
            
            // Use setTimeout to allow audio element to update with new src
            setTimeout(() => {
                if (audioRef.current) {
                    audioRef.current.play().then(() => {
                        setIsPlaying(true);
                        updateMusicState({
                            isPlaying: true,
                            selectedTrack: track,
                            volume: isMuted ? 0 : volume
                        });
                    }).catch(error => {
                        console.error('Auto-play failed:', error);
                        updateMusicState({
                            isPlaying: false,
                            selectedTrack: track,
                            volume: isMuted ? 0 : volume
                        });
                    });
                }
            }, 200);
        }
    };

    const handleVolumeChange = (value) => {
        setVolume(value);
        if (audioRef.current) {
            audioRef.current.volume = isMuted ? 0 : value;
        }
        
        updateMusicState({
            isPlaying,
            selectedTrack,
            volume: isMuted ? 0 : value
        });
    };

    const handleMute = () => {
        setIsMuted(!isMuted);
        if (audioRef.current) {
            audioRef.current.volume = !isMuted ? 0 : volume;
        }
        
        updateMusicState({
            isPlaying,
            selectedTrack,
            volume: !isMuted ? 0 : volume
        });
    };

    const handleUpload = (data) => {
            const newTrack = {
            id: Date.now(),
            name: data.originalname,
            url: data.location,
            duration: 0 // Will be set when loaded
        };
        if (onUpload) {
            onUpload("music", newTrack);
        }
    };

    return (
        <div className="music-panel">
            {/* Audio element */}
            {selectedTrack && (
                <audio 
                    ref={audioRef} 
                    src={selectedTrack.url}
                    preload="metadata"
                />
            )}

            {/* Upload Section */}
            <div className="music-upload-section">
                <h4>Music Library</h4>
                <div className="music-grid">
                    {/* Upload Item */}
                    <FileUpload
                        allowedTypes={['audio/mp3','audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/aac']}
                        maxFileSize={10}
                        showUploadList={false}
                        onComplete={res => handleUpload(res.fileList[0]?.response?.data)}
                        className="upload-container"
                        preview={false}
                        text="Upload Audio"
                        data={{ bucket: "viblica-assets" }}
                    >
                        <div className="music-item upload-item">
                            <div className="upload-content">
                                <IoMdAdd className="upload-plus-icon" />
                                <div className="upload-text">Add Music</div>
                            </div>
                        </div>
                    </FileUpload>

                    {/* Music Items */}
                    {musicLibrary.map((track, index) => {
                        const isSelected = selectedTrack?.name === track.name;
                        const isCurrentlyPlaying = isSelected && isPlaying;
                        console.log(`Track ${track.name}: selected=${isSelected}, playing=${isCurrentlyPlaying}`);
                        
                        return (
                            <div
                                key={track.url || index}
                                className={`music-item ${isSelected ? 'active' : ''} ${isCurrentlyPlaying ? 'playing' : ''}`}
                                onClick={() => handleTrackClick(track)}
                            >
                                <div className="music-item-content">
                                    <Icon 
                                        type={isCurrentlyPlaying ? "pause-circle" : "play-circle"} 
                                        className="music-icon" 
                                    />
                                    <div className="music-info">
                                        <div className="music-name">{track.name}</div>
                                    </div>
                                </div>
                                {/* Delete button - only show for owner */}
                                {(track.owner === gState?.user?._id) && (
                                    <button
                                        className="delete-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (onDelete) onDelete("music", track);
                                        }}
                                        title="Delete music track"
                                    >
                                        <Icon type="delete" />
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Volume Control - Only show when music is selected */}
            {selectedTrack && (
                <div className="volume-controls">
                    <div className="volume-section">
                        <Button 
                          
                            onClick={handleMute}
                            className="volume-button"
                            size="small"
                        >
                        {isMuted ? <IoMdVolumeOff color='black' size={20} /> : <IoMdVolumeHigh  color='black'  size={20} />}</Button>
                        <Slider
                            min={0}
                            max={1}
                            step={0.1}
                            value={volume}
                            onChange={handleVolumeChange}
                            className="volume-slider"
                        />
                        <span className="volume-display">{Math.round(volume * 100)}%</span>
                    </div>
                </div>
            )}

            {/* Empty State */}
            {(!musicLibrary || musicLibrary.length === 0) && (
                <div className="empty-state">
                    <Icon type="sound" className="empty-icon" />
                    <div className="empty-text">No music uploaded yet</div>
                    <div className="empty-hint">Upload your first audio file to get started</div>
                </div>
            )}
        </div>
    );
};

export default MusicPanel;